import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Alert,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { applyLeave, getHolidaysByYear, getMyLeaveBalance } from "../../Api/ApiUrlConst";
import { ApplyLeaveDto, LeaveBalanceDto } from "../../services/leaveModels";

const ApplyLeave: React.FC = () => {
  const navigate = useNavigate();

  const [leaveTypes, setLeaveTypes] = useState<LeaveBalanceDto[]>([]);
  const [form, setForm] = useState<ApplyLeaveDto>({
    leaveTypeId: 0,
    fromDate: "",
    toDate: "",
    isHalfDay: false,
    reason: "",
  });
  const [holidays, setHolidays] = useState<string[]>([]);
  const [backendDays, setBackendDays] = useState<number | null>(null);

  const [calculatedDays, setCalculatedDays] = useState<number>(0);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  useEffect(() => {
    loadLeaveTypes();
  }, []);

  const loadLeaveTypes = async () => {
    try {
      const res = await getMyLeaveBalance();
      console.log("Leave balance response:", res.data);
      setLeaveTypes(res.data);
    } catch (err) {
      console.error("Leave balance error:", err);
    }
  };


  // 🧠 Simple UI-level calculation (backend is final authority)
  useEffect(() => {
    if (form.isHalfDay && form.fromDate) {
      setForm(prev => ({
        ...prev,
        toDate: prev.fromDate
      }));
    }
  }, [form.fromDate, form.isHalfDay]);


  useEffect(() => {
    if (!form.fromDate) return;

    const year = new Date(form.fromDate).getFullYear();

    getHolidaysByYear(year).then((res) => {
      setHolidays(res.data.map((h) => h.date)); // yyyy-MM-dd
    });
  }, [form.fromDate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;

    let updatedForm = {
      ...form,
      [name]:
        name === "leaveTypeId"
          ? Number(value)
          : type === "checkbox"
            ? checked
            : value,
    };

    // ✅ Half day auto set toDate
    if (name === "isHalfDay" && checked) {
      updatedForm.toDate = updatedForm.fromDate;
    }

    // ✅ Validation AFTER state prepared
    if ((name === "fromDate" || name === "toDate") && value) {
      if (isInvalidLeaveDate(value)) {
        setError("Selected date is a holiday or weekend.");
        return;
      }
    }

    setError("");
    setForm(updatedForm);
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const res = await applyLeave(form);

      setBackendDays(res.data.totalDays); // ✅ backend truth
      setSuccess(
        `Leave applied successfully. Backend calculated days: ${res.data.totalDays}`
      );

      setTimeout(() => navigate("/leave"), 1500);
    } catch (err: any) {
      setError(
        err.response?.data?.title ||
        err.response?.data?.message ||
        "Failed to apply leave."
      );
    }
  };

  const isInvalidLeaveDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDay();

    // Weekend
    if (day === 0 || day === 6) return true;

    // Holiday
    if (holidays.includes(dateStr)) return true;

    return false;
  };

  return (
    <div className="container mt-4">
      {backendDays !== null && (
        <Alert color="info">
          Backend Calculated Days: <strong>{backendDays}</strong>
        </Alert>
      )}

      <Card className="shadow border-0 rounded-4">
        {/* Header */}
        <div
          className="p-3 text-white rounded-top"
          style={{
            background: "linear-gradient(45deg, #4e73df, #224abe)",
          }}
        >
          <h4 className="mb-0">Apply Leave</h4>
        </div>

        <CardBody className="p-4">
          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{success}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md="3">
                <FormGroup>
                  <Label>Leave Type</Label>
                  <Input
                    type="select"
                    name="leaveTypeId"
                    value={form.leaveTypeId}
                    onChange={handleChange}
                  >
                    <option value={0}>-- Select --</option>

                    {leaveTypes
                      .filter(l => ["EL", "SL"].includes(l.leaveTypeCode))
                      .map(l => (
                        <option key={l.leaveTypeId} value={l.leaveTypeId}>
                          {l.leaveTypeName} ({l.remainingDays} Remaining)
                        </option>
                      ))}
                  </Input>
                </FormGroup>

              </Col>
              <Col md="3">
                <FormGroup check className="mb-3">
                  <Input
                    style={{ marginTop: "40px" }}
                    type="checkbox"
                    name="isHalfDay"
                    checked={form.isHalfDay}
                    onChange={handleChange}
                  />{" "}
                  <Label style={{ marginTop: "35px" }} check>Half Day Leave</Label>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="mb-3">
                  <Label>Reason</Label>
                  <Input
                    type="textarea"
                    name="reason"
                    rows={8}
                    value={form.reason}
                    onChange={handleChange}
                    placeholder="Enter leave reason..."
                  />

                </FormGroup>
              </Col>


            </Row>



            <Row style={{ marginTop: "-150px" }}>
              <Col md="2">
                <FormGroup>
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    name="fromDate"
                    value={form.fromDate}
                    onChange={handleChange}
                  />
                </FormGroup>
              </Col>

              <Col md="2">
                <FormGroup>
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    name="toDate"
                    value={form.isHalfDay ? form.fromDate : form.toDate}
                    onChange={handleChange}
                    disabled={form.isHalfDay}
                  />
                </FormGroup>
              </Col>

              <Col md="3">
                <Label >Leave Days count</Label>
                <FormGroup className="mb-3">
                  <div
                    className="mb-2 p-1 rounded text-center"
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e0e0e0",
                      width: "130px"
                    }}
                  >
                    <h3 className="mb-0 text-primary">{calculatedDays}</h3>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            {/* Days Display */}


            <div className="d-flex gap-3">
              <Button color="primary" type="submit">
                Apply Leave
              </Button>

              <Button
                color="secondary"
                type="button"
                onClick={() => navigate("/leave")}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );

};

export default ApplyLeave;
